import React, { useState, useEffect } from "react";
import { Button, Drawer, IconButton, List, ListItemButton } from "@mui/material";
import "./optionsAdmin.css";
import { set, values } from "lodash";
import { Delete, Warning } from "@mui/icons-material";

function SingleOptionAdmin(props) {
  const [optionValue, setOptionValue] = useState(props.optionValue);
  const [optionName, setOptionName] = useState(props.optionName);
  const [status, setStatus] = useState("");
  const [updateOrderVisible, setUpdateOrderVisible] = useState(false);
  const [wipOrder, setWipOrder] = useState(new Map());
  const [optionChanged, setOptionChanged] = useState(false);

  useEffect(() => {
    Object.entries(optionValue.values ?? {}).forEach(([valueName, valInfo]) => {
      wipOrder.set(valueName, valInfo.displayOrder || 0);
    });
  }, []);

  function processCondition(condition) {
    try {
      return JSON.parse(condition);
    } catch (err) {
      return { parseError: err.message };
    }
  }

  const handleChange = (e) => {
    setOptionChanged(true);
    const { name, value, type, checked } = e.target;
    setOptionValue((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "condition" ? processCondition(value) : value,
    }));
  };

  const handleOptionNameChange = (e) => {
    setOptionChanged(true);
    setOptionName(e.target.value);
  };

  const handleOptionDisplayOrderChange = (newValue) => {
    setOptionChanged(true);
    setOptionValue((prev) => ({
      ...prev,
      displayOrder: isNaN(Number(newValue)) || Number(newValue) < 0 ? 0 : Number(newValue),
    }));
  };

  const handleValueChange = (valueName, field, newValue) => {
    setOptionChanged(true);
    setOptionValue((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [valueName]: {
          ...prev.values[valueName],
          [field]: newValue,
        },
      },
    }));
  };

  const handleDefaultValueChange = (valueName) => {
    setOptionChanged(true);
    setOptionValue((prev) => ({
      ...prev,
      default: valueName,
    }));
  };

  const handleValueNameChange = (oldValueName, newValueName) => {
    setOptionChanged(true);
    setOptionValue((prev) => {
      const updatedValues = { ...prev.values };
      updatedValues[newValueName] = updatedValues[oldValueName];
      delete updatedValues[oldValueName];
      return {
        ...prev,
        values: updatedValues,
      };
    });
  };

  const handleValueNameBlur = (oldValueName, newValueName) => {
    if (oldValueName !== newValueName && newValueName.trim() !== "") {
      handleValueNameChange(oldValueName, newValueName);
    }
  };

  const handleAddValue = () => {
    setOptionChanged(true);
    const newValueName = `value${Object.keys(optionValue.values ?? {}).length + 1}`;
    const maxDisplayOrder = Math.max(0, ...Object.values(optionValue.values ?? {}).map((v) => v.displayOrder || 0));
    setOptionValue((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [newValueName]: {
          displayName: "",
          hidden: false,
          displayOrder: maxDisplayOrder + 1,
        },
      },
    }));
    setWipOrder((prev) => new Map(prev).set(newValueName, maxDisplayOrder + 1));
  };

  const handleDisplayOrderChange = (valueName, newValue) => {
    setWipOrder((prev) => {
      if (prev.has(valueName) && prev.get(valueName) !== newValue) {
        if (newValue !== "") {
          setUpdateOrderVisible(true);
        }
        return new Map(prev).set(valueName, newValue);
      }
      return prev;
    });
  };

  const handleDisplayOrderUpdate = () => {
    setOptionChanged(true);
    setUpdateOrderVisible(false);
    setOptionValue((prev) => {
      const updatedValues = { ...prev.values };
      for (const [valueName, displayOrder] of wipOrder) {
        let coerced = Number(displayOrder);
        let boundedValue = isNaN(coerced) || coerced < 0 ? 0 : coerced;
        updatedValues[valueName] = {
          ...updatedValues[valueName],
          displayOrder: boundedValue,
        };
      }
      return {
        ...prev,
        values: updatedValues,
      };
    });
    setWipOrder((prev) => {
      const newMap = new Map(prev);
      for (const [valueName, displayOrder] of prev) {
        let coerced = Number(displayOrder);
        let boundedValue = isNaN(coerced) || coerced < 0 ? 0 : coerced;
        newMap.set(valueName, boundedValue);
      }
      return newMap;
    });
  };

  const handleValueRemove = (valueName) => {
    setOptionChanged(true);
    setOptionValue((prev) => {
      const updatedValues = { ...prev.values };
      delete updatedValues[valueName];
      return {
        ...prev,
        values: updatedValues,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("Updating...");
    try {
      const response = await fetch("/api/update-option", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          optionName,
          optionValue,
          projectSlug: props.projectSlug,
        }),
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      console.log("Update successful:", data);
      setStatus("Update success!");
      props.onNewOptionSaved(optionName);
      setOptionChanged(false);
      setTimeout(() => setStatus(""), 2000);
    } catch (error) {
      console.error("Error updating option:", error);
      setStatus(JSON.stringify(error));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={"single-option-container" + (optionChanged ? " changed" : "")}>
        <div>
          <span className="option-admin-option-name">Option name: </span>
          <input type="text" value={optionName} onChange={handleOptionNameChange} />
        </div>
        <div className="option-json-display">{props.showJson ? JSON.stringify(optionValue) : null}</div>
        <div>
          Display name: <input type="text" name="displayName" value={optionValue.displayName} onChange={handleChange} />
        </div>
        <div>
          Display order:
          <input
            type="text"
            name="displayOrder"
            value={optionValue.displayOrder || 0}
            onChange={(e) => handleOptionDisplayOrderChange(e.target.value)}
          />
        </div>
        <div>
          Type:
          <input
            type="radio"
            name="type"
            value="select"
            checked={optionValue.type === "select"}
            onChange={handleChange}
            id="typeSelectChoice"
          />
          <label htmlFor="typeSelectChoice">Select</label>
          <input
            type="radio"
            name="type"
            value="checkbox"
            checked={optionValue.type === "checkbox"}
            onChange={handleChange}
            id="typeCheckboxChoice"
          />
          <label htmlFor="typeCheckboxChoice">Checkbox</label>
        </div>
        <div>
          Hidden: <input type="checkbox" name="hidden" checked={optionValue.hidden} onChange={handleChange} />
        </div>
        <div>
          Condition:{" "}
          <input
            type="text"
            name="condition"
            defaultValue={JSON.stringify(optionValue.condition)}
            onChange={handleChange}
          />
        </div>
        <div className="option-values-container">
          {optionValue.type === "select" ? <span style={{ fontWeight: "bold" }}>Values:</span> : null}
          {Object.entries(optionValue.values ?? {})
            .toSorted((a, b) => a[1].displayOrder - b[1].displayOrder)
            .map(([valueName, valInfo], index) => {
              return (
                <div key={"option-" + index} className="single-option-value-container">
                  <div>
                    Value name:{" "}
                    <input
                      type="text"
                      value={valueName}
                      onChange={(e) => handleValueNameBlur(valueName, e.target.value)}
                    />
                  </div>
                  <div>
                    Display name:
                    <input
                      type="text"
                      value={valInfo.displayName}
                      onChange={(e) => handleValueChange(valueName, "displayName", e.target.value)}
                    />
                  </div>
                  <div>
                    Display order:
                    <input
                      type="text"
                      value={wipOrder.get(valueName)}
                      onChange={(e) => handleDisplayOrderChange(valueName, e.target.value)}
                    />
                    {updateOrderVisible ? (
                      <button type="button" onClick={handleDisplayOrderUpdate}>
                        Update order
                      </button>
                    ) : null}
                  </div>
                  <div>
                    <label>Hidden</label>
                    <input
                      type="checkbox"
                      checked={valInfo.hidden}
                      onChange={(e) => handleValueChange(valueName, "hidden", e.target.checked)}
                    />
                  </div>
                  <div>
                    <label>Is default value</label>
                    <input
                      type="checkbox"
                      checked={optionValue.default === valueName}
                      onChange={(e) => handleDefaultValueChange(valueName)}
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => handleValueRemove(valueName)}
                      disabled={optionValue.default === valueName}
                    >
                      Remove value
                    </button>
                  </div>
                </div>
              );
            })}
          {optionValue.type === "select" && (
            <button type="button" onClick={handleAddValue}>
              Add new value
            </button>
          )}
        </div>
        <div className="option-admin-actions">
          <Button variant="contained" size="small" type="submit">
            {optionValue.isNewlyAdded ? "Save option" : "Update option"}
          </Button>
          {optionValue.isNewlyAdded ? (
            <Button
              variant="contained"
              size="small"
              type="button"
              color="error"
              onClick={() => props.onNewOptionRemoved(optionName)}
            >
              <Delete />
            </Button>
          ) : null}
          {optionChanged ? (
            <span className="option-changed-indicator">
              <Warning color="warning" />
              Unsaved changes
            </span>
          ) : null}
        </div>
        <div>{status}</div>
      </div>
    </form>
  );
}

function OptionsAdmin(props) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(props.options ?? {});
  const [showJson, setShowJson] = useState(
    localStorage.getItem("optionsAdminShowJson") ? JSON.parse(localStorage.getItem("optionsAdminShowJson")) : false,
  );

  const handleAddOption = () => {
    const newOptionName = `option${Object.keys(options).length + 1}`;
    const newOption = {
      displayName: "",
      displayOrder: Math.max(0, ...Object.values(options).map((o) => o.displayOrder || 0)) + 1,
      type: "select",
      hidden: false,
      condition: {},
      values: {},
      isNewlyAdded: true,
    };
    setOptions((prev) => ({
      ...prev,
      [newOptionName]: newOption,
    }));
  };

  const handleShowJsonToggle = () => {
    setShowJson((prev) => {
      localStorage.setItem("optionsAdminShowJson", JSON.stringify(!prev));
      return !prev;
    });
  };

  const handleNewOptionRemoved = (optionName) => {
    setOptions((prev) => {
      const updatedOptions = { ...prev };
      delete updatedOptions[optionName];
      return updatedOptions;
    });
  };

  const handleNewOptionSaved = (optionName) => {
    setOptions((prev) => {
      const updatedOptions = { ...prev };
      if (updatedOptions[optionName]) {
        updatedOptions[optionName].isNewlyAdded = false;
      }
      return updatedOptions;
    });
  };

  return (
    <div className="options-admin-main-container">
      <Button variant="contained" onClick={() => setOpen((currVal) => !currVal)}>
        {open ? "Close options editor" : "Open options editor"}
      </Button>
      <div style={{ display: open ? "block" : "none" }}>
        Show JSON:
        <input type="checkbox" checked={showJson} onChange={handleShowJsonToggle} />
      </div>
      <div className="options-admin-options-container" style={{ display: open ? "block" : "none" }}>
        {Object.entries(options ?? {})
          .filter((o) => o[0] !== "playerColors")
          .toSorted((a, b) => a[1].displayOrder - b[1].displayOrder)
          .map((o) => (
            <SingleOptionAdmin
              key={o[0]}
              optionName={o[0]}
              optionValue={o[1]}
              projectSlug={props.projectSlug}
              showJson={showJson}
              onNewOptionRemoved={handleNewOptionRemoved}
              onNewOptionSaved={handleNewOptionSaved}
            />
          ))}
        <Button
          variant="contained"
          size="small"
          type="button"
          style={{ marginTop: "0.5rem" }}
          onClick={handleAddOption}
        >
          Add new option
        </Button>
      </div>
    </div>
  );
}

export { SingleOptionAdmin, OptionsAdmin };
